import React from 'react'
import { Outlet, useLocation, useNavigationType } from 'react-router-dom'
import { AppContext } from '@/components/base/appcontext'
import { useReferrerBack } from '@/hooks/useReferrerBack'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { ArrowLeft } from 'lucide-react'

type PageProps = {
    hasBackButton?: boolean
    headerSlug?: string
    headerAction?: React.ReactElement
    children: React.ReactElement
}

/** Matches .page-enter in index.css. */
const ENTER_MS = 160

/** Scroll offsets of visited entries, by history key, for back navigation. */
const scrollPositions = new Map<string, number>()

/**
 * How long a restore keeps following the content as it streams in. Past
 * this the page stays where it got to; the saved offset is kept for the
 * next visit.
 */
const RESTORE_WINDOW_MS = 3000

/**
 * Pages scroll inside their own container, so the browser's and React
 * Router's scroll restoration never see it. Back (POP) returns to where
 * the entry was left, following deferred content as it fills in; a new
 * screen starts at the top; a replace stays where it is.
 *
 * Offsets are recorded only from the user's own scrolling, never from a
 * restore in progress: content that is still short caps scrollTop, and
 * recording that would replace the real position with the capped one.
 */
function useScrollMemory(ref: React.RefObject<HTMLDivElement | null>) {
    const location = useLocation()
    const navigationType = useNavigationType()
    const restoring = React.useRef(false)

    React.useEffect(() => {
        const el = ref.current
        if (!el) return
        const key = location.key
        let frame = 0
        const onScroll = () => {
            if (restoring.current) return
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => {
                scrollPositions.set(key, el.scrollTop)
            })
        }
        el.addEventListener('scroll', onScroll, { passive: true })
        return () => {
            cancelAnimationFrame(frame)
            el.removeEventListener('scroll', onScroll)
        }
    }, [ref, location.key])

    React.useLayoutEffect(() => {
        const el = ref.current
        if (!el) return
        // A replace keeps the screen and its scroll (a list loading its
        // next page, a cleaned-up state). It also mints a new entry key:
        // record the kept offset under it, or back to this entry would find
        // nothing unless the user happened to scroll again first.
        if (navigationType === 'REPLACE') {
            scrollPositions.set(location.key, el.scrollTop)
            return
        }
        const target =
            navigationType === 'POP' ? scrollPositions.get(location.key) : 0
        if (!target) {
            el.scrollTop = 0
            return
        }

        restoring.current = true
        let timer = 0
        // jsdom and old webviews lack ResizeObserver: the timer still ends it.
        const observer =
            typeof ResizeObserver === 'function'
                ? new ResizeObserver(() => apply())
                : undefined
        const finish = () => {
            restoring.current = false
            observer?.disconnect()
            window.clearTimeout(timer)
            el.removeEventListener('touchstart', finish)
            el.removeEventListener('wheel', finish)
        }
        function apply() {
            el!.scrollTop = target!
            if (Math.abs(el!.scrollTop - target!) < 2) finish()
        }
        // The user taking over ends the restore where they are.
        el.addEventListener('touchstart', finish, { passive: true })
        el.addEventListener('wheel', finish, { passive: true })
        // Deferred rows land inside the scroll container: each time its
        // content grows, try again.
        for (const child of Array.from(el.children)) observer?.observe(child)
        timer = window.setTimeout(finish, RESTORE_WINDOW_MS)
        apply()
        return finish
        // Only a new entry moves the scroll; same-entry state updates
        // (replace with load-more state) keep it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key])
}

/**
 * The scrolling body of a screen, remounted per pathname: React keeps one
 * Page instance across routes (same element type), so without the key a
 * new screen would inherit the old one's entrance state and scroll.
 */
function PageBody({ children }: { children: React.ReactNode }) {
    const navigationType = useNavigationType()
    const containerRef = React.useRef<HTMLDivElement>(null)
    useScrollMemory(containerRef)

    // Only a screen arriving forward animates; one returned to (POP) is
    // already known and shows as it was. The class must be present on
    // first paint: adding it later (e.g. in an effect) paints the content
    // once, then snaps to the keyframe's `from` state.
    const [entering] = React.useState(() => navigationType !== 'POP')

    return (
        <div
            ref={containerRef}
            style={
                entering
                    ? {
                          animationDuration: `${ENTER_MS}ms`,
                          animationFillMode: 'both'
                      }
                    : undefined
            }
            className={`${entering ? 'page-enter' : ''} flex-1 min-h-0 overflow-auto rounded-lg pb-16 px-4`}
        >
            {children}
        </div>
    )
}

function Page(props: PageProps) {
    const appContext = React.useContext(AppContext)
    const location = useLocation()
    const handleBack = useReferrerBack()

    // The header stays put: the chrome is the steady frame the content
    // changes inside.
    return (
        <div className="flex h-screen overflow-hidden flex-col flex-nowrap content-center justify-start items-stretch grow">
            {props.headerSlug && (
                <div className="flex flex-row justify-between items-center gap-2 px-3 py-1.5">
                    {props.hasBackButton && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={handleBack}
                            aria-label={appContext.geti18n(
                                'base.error.button.back.text'
                            )}
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </Button>
                    )}
                    <Typography variant="body-sm">
                        {appContext.geti18n(props.headerSlug)}
                    </Typography>
                    {props.headerAction}
                </div>
            )}

            <PageBody key={location.pathname}>{props.children}</PageBody>
        </div>
    )
}

export function ChildPage(props: Omit<PageProps, 'children'>) {
    return (
        <Page {...props}>
            <Outlet />
        </Page>
    )
}

export default Page
